import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Skeleton } from "@/components/Skeleton";
import {
  History, Sparkles, User, MapPin, CheckCircle2, XCircle,
  ChevronRight, ArrowRight, FileWarning, Filter, Download, Loader2,
} from "lucide-react";

/**
 * ProgrammeChangeHistory (iter113) — unified read-only timeline.
 *
 * Reads GET /api/patients/{id}/history which merges programme_versions,
 * ai_proposals and roadmap_transitions server-side. This component NEVER
 * mutates state on open. Chronological newest-first, filterable, with a
 * "Load more" cursor.
 */
const FILTERS = [
  ["all", "All"],
  ["programme", "Programme changes"],
  ["proposals", "AI proposals"],
  ["roadmap", "Roadmap & criteria"],
];

const KIND_META = {
  programme_replaced: { label: "Programme replaced", Icon: User, tone: "blue" },
  ai_proposal_applied: { label: "AI change applied", Icon: Sparkles, tone: "orange" },
  ai_proposal_created: { label: "AI proposal", Icon: Sparkles, tone: "amber" },
  ai_proposal_approved: { label: "AI proposal approved", Icon: CheckCircle2, tone: "green" },
  ai_proposal_rejected: { label: "AI proposal rejected", Icon: XCircle, tone: "red" },
  roadmap_assigned: { label: "Roadmap assigned", Icon: MapPin, tone: "orange" },
  roadmap_advance: { label: "Phase advanced", Icon: ArrowRight, tone: "green" },
  roadmap_regress: { label: "Phase regressed", Icon: ArrowRight, tone: "amber" },
  roadmap_skip: { label: "Phase skipped", Icon: ArrowRight, tone: "amber" },
  criterion_confirmed: { label: "Criterion confirmed", Icon: CheckCircle2, tone: "green" },
  criterion_revoked: { label: "Criterion revoked", Icon: XCircle, tone: "amber" },
};

const TONE = {
  blue:   "text-sky-300 border-sky-500/40 bg-sky-500/10",
  orange: "text-[#f47a20] border-[#f47a20]/50 bg-[#f47a20]/10",
  amber:  "text-amber-300 border-amber-500/40 bg-amber-500/10",
  green:  "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  red:    "text-red-300 border-red-500/40 bg-red-500/10",
};

const FIELD_LABELS = {
  sets: "Sets", reps: "Reps", rest_seconds: "Rest",
  duration_seconds: "Duration", weight: "Load", tempo: "Tempo",
  pain_limit: "Pain limit", progression: "Progression",
  regression: "Regression", frequency_per_week: "Frequency",
  side: "Side", notes: "Notes",
};

function fmtValue(field, v) {
  if (v == null) return <span className="text-zinc-500 italic">not set</span>;
  if (field === "rest_seconds" || field === "duration_seconds") return `${v}s`;
  if (field === "frequency_per_week") return `${v}/week`;
  if (Array.isArray(v)) return v.length ? v.join(", ") : <span className="text-zinc-500 italic">none</span>;
  return String(v);
}

function ExerciseChange({ change }) {
  const nameNow = change.exercise_name || change.exercise_id?.slice(0, 8) || "exercise";
  const namePrior = change.prior_exercise_name;
  const header = ({
    added: `+ Added · ${nameNow}`,
    removed: `− Removed · ${nameNow}`,
    substituted: `↔ Substituted · ${namePrior || "prior"} → ${nameNow}`,
    modified: `✎ ${nameNow}`,
    moved: `↕ Reordered · ${nameNow}`,
  }[change.action]) || nameNow;
  return (
    <div className="border-l-2 border-white/10 pl-3">
      <div className="text-[11px] text-zinc-300">{header}
        {(change.position || change.prior_position) && (
          <span className="text-zinc-500 ml-1">
            (pos {change.prior_position || "—"}
            {change.position && change.action !== "removed" ? ` → ${change.position}` : ""})
          </span>
        )}
      </div>
      {change.diff && change.diff.length > 0 && (
        <ul className="text-xs font-mono space-y-1 mt-1">
          {change.diff.map((d) => (
            <li key={d.field} className="flex items-center gap-2">
              <span className="text-zinc-400 min-w-[92px]">
                {FIELD_LABELS[d.field] || d.field}:
              </span>
              <span className="text-zinc-500 line-through">{fmtValue(d.field, d.before)}</span>
              <ArrowRight size={10} className="text-zinc-500 shrink-0" />
              <span className="text-[#f47a20]">{fmtValue(d.field, d.after)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function ProgrammeChangeHistory({ patientId }) {
  const [state, setState] = useState({
    loading: true, error: null, events: [], nextCursor: null,
    trackingStartedAt: null,
  });
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState({});
  const [pdfBusy, setPdfBusy] = useState(false);

  // Full programme change history → branded PDF (clinician/admin, caseload-enforced server-side).
  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/patients/${patientId}/history/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `programme-history-${patientId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("History PDF downloaded");
    } catch (e) {
      let msg = "Could not download the history PDF.";
      if (e?.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text())?.detail || msg; } catch { /* keep default */ }
      } else if (e?.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      toast.error(msg);
    } finally { setPdfBusy(false); }
  };

  const load = async ({ append = false, cursor = null } = {}) => {
    setState((s) => ({ ...s, loading: !append, error: null }));
    try {
      const { data } = await api.get(`/patients/${patientId}/history`, {
        params: { filter, limit: 25, cursor: cursor || undefined },
      });
      setState((s) => ({
        loading: false, error: null,
        events: append ? [...s.events, ...(data.events || [])] : (data.events || []),
        nextCursor: data.next_cursor || null,
        trackingStartedAt: data.tracking_started_at || null,
      }));
    } catch (e) {
      setState((s) => ({
        ...s, loading: false,
        error: e?.response?.data?.detail || "Could not load history.",
      }));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [patientId, filter]);

  const groupedByProgramme = useMemo(() => {
    const groups = new Map();
    for (const e of state.events) {
      const key = e.programme_id || "unassigned";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(e);
    }
    return groups;
  }, [state.events]);

  return (
    <div className="fl-card fl-card-elevated p-5 mt-4" data-testid="programme-change-history">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <History size={16} className="text-[#f47a20]" />
        <div className="fl-heading text-xl">Programme change history</div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={downloadPdf}
            disabled={pdfBusy || state.loading || (state.events.length === 0 && !state.error)}
            className="fl-btn-ghost text-xs"
            data-testid="history-download-pdf"
            title="Download the full programme change history as a branded PDF"
          >
            {pdfBusy ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Download History PDF
          </button>
          <div className="flex items-center gap-1 flex-wrap" role="tablist" aria-label="Filter history">
          <Filter size={12} className="text-zinc-500 mr-1" />
          {FILTERS.map(([id, label]) => (
            <button
              key={id}
              role="tab"
              aria-selected={filter === id}
              data-testid={`history-filter-${id}`}
              onClick={() => setFilter(id)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition
                ${filter === id
                  ? "bg-[#f47a20] text-black"
                  : "text-zinc-400 hover:text-zinc-100"}`}
            >
              {label}
            </button>
          ))}
          </div>
        </div>
      </div>

      {state.loading && (
        <div className="space-y-2" data-testid="history-loading">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 rounded" />)}
        </div>
      )}

      {!state.loading && state.error && (
        <div className="text-sm text-red-300" data-testid="history-error">
          <FileWarning size={14} className="inline mr-1" /> {state.error}
          <button onClick={() => load()} className="fl-btn-ghost text-xs ml-2">Retry</button>
        </div>
      )}

      {!state.loading && !state.error && state.events.length === 0 && (
        <div className="text-sm text-zinc-500" data-testid="history-empty">
          No recorded changes yet.
          {state.trackingStartedAt && (
            <span className="block text-[11px] text-zinc-600 mt-1">
              Detailed change tracking is available from{" "}
              {new Date(state.trackingStartedAt).toLocaleDateString("en-GB")}.
            </span>
          )}
        </div>
      )}

      {!state.loading && !state.error && state.events.length > 0 && (
        <ol className="relative border-l border-white/10 ml-2 pl-4 space-y-3" data-testid="history-timeline">
          {state.events.map((e) => {
            const meta = KIND_META[e.kind] || { label: e.kind, Icon: History, tone: "blue" };
            const isOpen = !!expanded[e.id];
            const hasDetails = (e.diff && e.diff.length > 0)
                              || e.prior_prescription || e.new_prescription
                              || e.reason;
            return (
              <li key={e.id} data-testid={`history-event-${e.id}`}
                  className="relative">
                <span className={`absolute -left-[22px] top-2 w-3 h-3 rounded-full border ${TONE[meta.tone]}`} />
                <div className="fl-card p-3 border border-white/5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full border ${TONE[meta.tone]}`}>
                      <meta.Icon size={10} /> {meta.label}
                    </span>
                    {e.status && e.status !== "applied" && (
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                        {e.status}
                      </span>
                    )}
                    {e.override && (
                      <span className="text-[10px] uppercase tracking-widest text-amber-400">
                        override
                      </span>
                    )}
                    <span className="text-[11px] text-zinc-500 ml-auto">
                      {e.at ? new Date(e.at).toLocaleString("en-GB") : "—"}
                    </span>
                  </div>
                  <div className="mt-2 text-sm text-zinc-200">{e.summary}</div>
                  <div className="mt-1 text-[11px] text-zinc-500">
                    by {e.actor || "Not recorded"}
                    {e.programme_id && (
                      <> · programme <span className="font-mono">{e.programme_id.slice(0, 8)}</span></>
                    )}
                  </div>

                  {hasDetails && (
                    <>
                      <button
                        onClick={() => setExpanded((s) => ({ ...s, [e.id]: !isOpen }))}
                        className="fl-btn-ghost text-[11px] mt-2"
                        data-testid={`history-expand-${e.id}`}
                      >
                        <ChevronRight size={11} className={`transition ${isOpen ? "rotate-90" : ""}`} />
                        {isOpen ? "Hide details" : "Show details"}
                      </button>
                      {isOpen && (
                        <div className="mt-2 space-y-2" data-testid={`history-details-${e.id}`}>
                          {e.reason && (
                            <div className="text-xs">
                              <span className="text-zinc-500">Reason: </span>
                              <span className="text-zinc-200">{e.reason}</span>
                            </div>
                          )}
                          {e.diff && e.diff.length > 0 && (
                            <ul className="text-xs font-mono space-y-1">
                              {e.diff.map((d) => (
                                <li key={d.field} className="flex items-center gap-2">
                                  <span className="text-zinc-400 min-w-[92px]">
                                    {FIELD_LABELS[d.field] || d.field}:
                                  </span>
                                  <span className="text-zinc-500 line-through">{fmtValue(d.field, d.before)}</span>
                                  <ArrowRight size={10} className="text-zinc-500 shrink-0" />
                                  <span className="text-[#f47a20]">{fmtValue(d.field, d.after)}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                          {(e.diff?.length ?? 0) === 0 && (e.prior_prescription || e.new_prescription) && (
                            <div className="text-[11px] text-zinc-500">
                              Full-programme replacement — see the previous programme record.
                            </div>
                          )}
                          {e.metadata_changes && e.metadata_changes.length > 0 && (
                            <div>
                              <div className="fl-label text-[10px] mb-1">Programme metadata</div>
                              <ul className="text-xs font-mono space-y-1">
                                {e.metadata_changes.map((d) => (
                                  <li key={d.field} className="flex items-center gap-2">
                                    <span className="text-zinc-400 min-w-[92px]">
                                      {FIELD_LABELS[d.field] || d.field}:
                                    </span>
                                    <span className="text-zinc-500 line-through">{fmtValue(d.field, d.before)}</span>
                                    <ArrowRight size={10} className="text-zinc-500 shrink-0" />
                                    <span className="text-[#f47a20]">{fmtValue(d.field, d.after)}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {e.exercise_changes && e.exercise_changes.length > 0 && (
                            <div>
                              <div className="fl-label text-[10px] mb-1">Exercise changes</div>
                              <div className="space-y-2">
                                {e.exercise_changes.map((c) => (
                                  <ExerciseChange key={`${c.programme_exercise_id || c.exercise_id}-${c.action}`} change={c} />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {state.nextCursor && (
        <button
          onClick={() => load({ append: true, cursor: state.nextCursor })}
          className="fl-btn-ghost text-xs mt-3"
          data-testid="history-load-more"
        >
          Load more
        </button>
      )}
    </div>
  );
}
